import type Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, titleStyle, readoutStyle, labelStyle } from '../theme';
import { createButton } from './button';
import { createListRow } from './panel';
import { maybeShowTutorial } from './adviserPanel';
import { plateKey, INCIDENT_PLATE, VESSEL_PLATE } from '../assets';
import { incidentType, type Incident } from '@core/mission/incident';
import type { Campaign } from '@core/campaign/campaign';
import type { Route } from '@core/map/navigation';
import { Voyage } from '@core/voyage/voyage';
import {
  RESEARCH_SHIP,
  SUBMARINE,
  SPEED_SETTINGS,
  type SpeedSetting,
  type VesselProfile,
} from '@core/voyage/profiles';
import { seaStateKey } from '@core/voyage/weather';
import type { OutcomeTier } from '@core/mission/outcome';
import { audio } from '../audio/engine';

/**
 * Einsatzplanung als Tafel ueber der Karte.
 *
 * Bis M8 war das ein eigener Bildschirm. Das kostete den entscheidenden
 * Zusammenhang: Man waehlte Fahrzeug und Fahrtstufe, ohne zu sehen, wohin der
 * Kurs eigentlich fuehrt. Der Umweg um die Baenke stand als Prozentzahl da.
 *
 * Jetzt bleibt die Karte sichtbar, und jede Aenderung zeichnet den Kurs neu —
 * `onCourse` reicht ihn an die Kartenszene zurueck. Man *sieht* den Umweg,
 * statt ihn zu lesen, und das ist derselbe Unterschied wie zwischen einer
 * Wettervorhersage und einem Blick aus dem Fenster.
 */

export interface BriefingOptions {
  readonly campaign: Campaign;
  readonly incident: Incident;
  /** Laeuft bei jedem Neuaufbau mit dem aktuell gerechneten Kurs. */
  readonly onCourse: (route: Route) => void;
  readonly onDepart: (order: {
    incident: Incident;
    vesselId: 'ship' | 'submarine';
    speed: SpeedSetting;
  }) => void;
  readonly onClose: () => void;
  /** Das Fahrzeug, das gerade betrachtet wird — es wird vorgewaehlt, wenn es fahren kann. */
  readonly preferredVessel?: 'ship' | 'submarine';
}

const PANEL_WIDTH = 640;

export function showBriefing(scene: Phaser.Scene, options: BriefingOptions): void {
  /*
   * Vorwahl: das betrachtete Fahrzeug, sofern es fahren kann — sonst das andere.
   *
   * Frueher stand hier immer das Forschungsschiff. Lag es fest, und man hatte
   * gerade das Tauchboot getankt und instand gesetzt, oeffnete die Planung
   * trotzdem mit dem blockierten Schiff, und „Ablegen“ blieb grau. Das sah
   * aus, als liesse sich gar nichts mehr losschicken.
   */
  const usable = (profile: VesselProfile): boolean => !options.campaign.needsAssistance(profile.id);
  const preferred = options.preferredVessel === 'submarine' ? SUBMARINE : RESEARCH_SHIP;
  const other = preferred === SUBMARINE ? RESEARCH_SHIP : SUBMARINE;
  let vessel: VesselProfile = usable(preferred) ? preferred : usable(other) ? other : preferred;
  let speed: SpeedSetting = 'cruise';
  let container: Phaser.GameObjects.Container | null = null;
  let shade: Phaser.GameObjects.Rectangle | null = null;

  const close = (): void => {
    container?.destroy();
    shade?.destroy();
    container = null;
    shade = null;
  };

  const build = (): void => {
    close();

    const { width, height } = scene.scale;
    /*
     * Schmal heisst: kein Schiffsbild im Kopf, Fahrzeuge untereinander statt
     * nebeneinander, und jede Zeile bricht um. Auf einem Telefon liefen
     * Kopfzeilen und Warnungen sonst rechts aus der Tafel, und die beiden
     * Knoepfe lagen uebereinander.
     */
    const compact = width < 700;
    const panelWidth = Math.min(PANEL_WIDTH, width - (compact ? 16 : 48));
    const wrap = { wordWrap: { width: panelWidth - 28 } };

    // Die Karte darunter leicht abdunkeln — sie soll sichtbar bleiben, dort
    // liegt schliesslich der Kurs, um den es geht. Der Schleier faengt
    // zugleich Klicks ab, die sonst auf einem Marker dahinter landeten.
    shade = scene.add
      .rectangle(0, 0, width, height, PALETTE.abyss, 0.35)
      .setOrigin(0, 0)
      .setDepth(1500)
      .setInteractive();

    const parts: Phaser.GameObjects.GameObject[] = [];
    const voyage = buildVoyage();
    const estimates = voyage.compareSettings();
    options.onCourse(voyage.route);

    const frame = scene.add.rectangle(0, 0, panelWidth, 0, PALETTE.panel, 0.97).setOrigin(0, 0);
    frame.setStrokeStyle(1, PALETTE.panelEdge);
    parts.push(frame);

    // Kopf: Bild des verunglueckten Fahrzeugs und die harten Zahlen.
    const plateHeight = 84;
    const plate = plateKey(INCIDENT_PLATE[options.incident.kind]);
    const hasPlate = !compact && scene.textures.exists(plate);
    if (hasPlate) {
      parts.push(
        scene.add
          .image(14, 14, plate)
          .setOrigin(0, 0)
          .setDisplaySize(plateHeight * 1.5, plateHeight),
      );
    }

    const headLeft = hasPlate ? 14 + plateHeight * 1.5 + 14 : 14;

    // Die Kopfzeilen setzen aufeinander auf, statt auf festen Zeilen zu
    // stehen: Mit vergroesserter Schrift liefen sie sonst ineinander.
    let headY = 14;
    const headLine = (text: string, style: object): void => {
      const object = scene.add.text(headLeft, headY, text, {
        ...style,
        wordWrap: { width: panelWidth - headLeft - 14 },
      });
      parts.push(object);
      headY += object.height + 4;
    };

    headLine(t('briefing.title'), titleStyle(16));
    headLine(
      `${t(incidentType(options.incident.kind).nameKey)} · ${options.incident.vesselName}`,
      readoutStyle(12),
    );
    headLine(
      `${t('call.age', { hours: Math.round(options.campaign.ageOf(options.incident)) })}  ·  ${t(
        'call.souls',
        { count: options.incident.souls },
      )}  ·  ${t('call.depth', { metres: Math.round(options.incident.depthMetres) })}`,
      readoutStyle(11, true),
    );
    headLine(departureLine(), {
      ...readoutStyle(11),
      color: options.campaign.isAtBase(vessel.id) ? PALETTE.inkFaint : TEXT_COLOUR.landmark,
    });

    let y = Math.max(hasPlate ? 14 + plateHeight : 0, headY) + 16;

    // Fahrzeugwahl
    const vesselLabel = scene.add.text(14, y, t('briefing.vessel'), labelStyle());
    parts.push(vesselLabel);
    y += vesselLabel.height + 6;

    let vesselRowHeight = 0;
    [RESEARCH_SHIP, SUBMARINE].forEach((profile, index) => {
      const tooDeep = options.incident.depthMetres > profile.maxWorkingDepthMetres;
      const detail = tooDeep
        ? `${profile.knots.cruise} kn · ${t('briefing.depth.short')}`
        : `${profile.knots.cruise} kn · ${t('briefing.distance', {
            miles: Math.round(options.campaign.distanceTo(options.incident, profile.id)),
          })}`;

      const columnLeft = compact ? 14 : 14 + index * ((panelWidth - 28) / 2);
      const columnWidth = compact ? panelWidth - 28 : (panelWidth - 28) / 2 - 8;
      const rowTop = compact ? y + index * (vesselRowHeight + 6) : y;
      const thumb = 32;
      const vesselPlate = plateKey(VESSEL_PLATE[profile.id]);
      const hasThumb = scene.textures.exists(vesselPlate);

      if (hasThumb) {
        parts.push(
          scene.add
            .image(columnLeft, rowTop, vesselPlate)
            .setOrigin(0, 0)
            .setDisplaySize(thumb * 1.5, thumb)
            .setAlpha(vessel.id === profile.id ? 1 : 0.45),
        );
      }

      const rowLeft = hasThumb ? columnLeft + thumb * 1.5 + 6 : columnLeft;
      const row = createListRow(scene, rowLeft, rowTop, {
        width: columnWidth - (rowLeft - columnLeft),
        height: 44,
        title: t(profile.nameKey),
        detail,
        selected: vessel.id === profile.id,
        onClick: () => {
          vessel = profile;
          build();
        },
      });
      parts.push(row);
      vesselRowHeight = Math.max(vesselRowHeight, row.height);
    });

    y += (compact ? vesselRowHeight * 2 + 6 : vesselRowHeight) + 14;

    // Fahrtstufen mit Vorausberechnung
    const speedLabel = scene.add.text(14, y, t('briefing.speed'), labelStyle());
    parts.push(speedLabel);
    y += speedLabel.height + 6;

    let chosenForecast: { tier: OutcomeTier; alternative: OutcomeTier | null } | null = null;
    SPEED_SETTINGS.forEach((setting) => {
      const estimate = estimates[setting];
      const forecast = options.campaign.forecastOnArrival(
        options.incident,
        options.campaign.clockHours + estimate.hours,
        vessel.id,
      );
      if (setting === speed) chosenForecast = forecast;

      const detail = [
        t('briefing.eta', { hours: Math.round(estimate.hours) }),
        t('briefing.fuel', {
          used: Math.round(estimate.fuelUsed),
          // Der Stand von jetzt — faehrt das Fahrzeug gerade, nicht der beim Ablegen.
          available: Math.round(options.campaign.conditionOf(vessel.id).fuel),
        }),
        estimate.hullLoss > 0.5
          ? t('briefing.hull', { value: Math.round(estimate.hullLoss) })
          : t('briefing.sea', { sea: t(seaStateKey(estimate.worstSeaState)) }),
      ].join('  ·  ');

      const row = createListRow(scene, 14, y, {
        width: panelWidth - 28,
        height: 42,
          title: t(`briefing.speed.${setting}`),
          detail,
          badge:
            forecast.alternative === null
              ? t(`outcome.${forecast.tier}`)
              : t('briefing.forecast.uncertain', {
                  tier: t(`outcome.${forecast.tier}`),
                  alternative: t(`outcome.${forecast.alternative}`),
                }),
          badgeColor:
            forecast.tier === 'rescue'
              ? '#8fd9c4'
              : forecast.tier === 'nothing'
                ? '#c9736f'
                : '#e0b070',
        selected: speed === setting,
        onClick: () => {
          speed = setting;
          build();
        },
      });
      parts.push(row);

      y += row.height + 6;
    });

    /*
     * Was der Einsatz der Akte bringen kann.
     *
     * Ein Hinweis zaehlt nur, wenn der laufende Akt genau diese Art braucht.
     * Ohne diese Zeile erfuhr man das erst am Fundort — man fuhr zwei Tage
     * hinaus und bekam nichts. Jetzt steht vorher da, ob es eine Wette auf
     * die Wahrheit ist oder eine reine Rettungsfahrt.
     */
    const forecastForSpeed = chosenForecast as { tier: OutcomeTier; alternative: OutcomeTier | null } | null;
    if (forecastForSpeed !== null) {
      const tiers = [forecastForSpeed.tier, forecastForSpeed.alternative].filter(
        (tier): tier is OutcomeTier => tier !== null,
      );
      const kinds = [...new Set(tiers.flatMap((tier) => options.campaign.usefulKindsFor(tier)))];
      const fileLine = scene.add.text(
        14,
        y,
        kinds.length > 0
          ? t('briefing.file.useful', { kinds: kinds.map((kind) => t(`slot.${kind}`)).join(', ') })
          : t('briefing.file.useless'),
        {
          ...readoutStyle(11),
          color: kinds.length > 0 ? TEXT_COLOUR.base : '#e0b070',
          wordWrap: { width: panelWidth - 28 },
        },
      );
      parts.push(fileLine);
      y += fileLine.height + 6;
    }

    /*
     * Was ein Kurswechsel aufgibt.
     *
     * Seit beide Fahrzeuge gleichzeitig fahren koennen, ist ein Klick auf
     * einen Notruf-Marker waehrend einer laufenden Fahrt kein Versehen mehr,
     * sondern eine Moeglichkeit — man kann umdisponieren. Aber die Stunden,
     * die schon in die alte Fahrt geflossen sind, sind weg, und das muss
     * dastehen, bevor man es tut.
     */
    const abandoning = options.campaign.abandonmentCost(vessel.id);
    if (abandoning !== null) {
      const what =
        abandoning.incident === null
          ? t('briefing.abandon.homebound')
          : `${t(incidentType(abandoning.incident.kind).nameKey)} · ${abandoning.incident.vesselName}`;

      parts.push(
        scene.add.text(
          14,
          y,
          t('briefing.abandon', { what, hours: Math.round(abandoning.spentHours) }),
          { ...readoutStyle(11), color: '#c9736f', wordWrap: { width: panelWidth - 28 } },
        ),
      );
      y += (parts[parts.length - 1] as Phaser.GameObjects.Text).height + 6;
    }

    // Warnungen
    const chosen = estimates[speed];
    if (voyage.route.detourFactor > 1.05) {
      parts.push(
        scene.add.text(
          14,
          y,
          t('briefing.detour', {
            percent: Math.round((voyage.route.detourFactor - 1) * 100),
            miles: Math.round(voyage.route.lengthNm),
          }),
          { ...readoutStyle(11), color: '#e0b070', ...wrap },
        ),
      );
      y += (parts[parts.length - 1] as Phaser.GameObjects.Text).height + 4;
    }
    if (!chosen.fuelSufficient) {
      parts.push(
        scene.add.text(14, y, t('briefing.fuel.short'), { ...readoutStyle(11), color: '#c9736f', ...wrap }),
      );
      y += (parts[parts.length - 1] as Phaser.GameObjects.Text).height + 4;
    }
    /*
     * Ein liegengebliebenes Fahrzeug faehrt nirgendwohin.
     *
     * Ohne diese Sperre liess es sich losschicken und strandete im selben
     * Augenblick wieder — oder fuhr mit 0 % Rumpf ein Stueck, das es nicht
     * haette fahren duerfen.
     */
    const disabled = options.campaign.needsAssistance(vessel.id);
    if (disabled) {
      const warning = scene.add.text(14, y, t('briefing.disabled'), {
        ...readoutStyle(11),
        color: '#c9736f',
        wordWrap: { width: panelWidth - 28 },
      });
      parts.push(warning);
      y += warning.height + 4;
    }
    if (options.incident.depthMetres > vessel.maxWorkingDepthMetres) {
      parts.push(
        scene.add.text(14, y, t('briefing.depth.warning'), {
          ...readoutStyle(11),
          color: '#e0b070',
          ...wrap,
        }),
      );
      y += (parts[parts.length - 1] as Phaser.GameObjects.Text).height + 4;
    }

    y += 10;
    // Zwei Knoepfe, die sich die Breite teilen — nie breiter als die Haelfte.
    const buttonWidth = Math.min(180, (panelWidth - 28 - 8) / 2);
    parts.push(
      createButton(scene, 14 + buttonWidth / 2, y + 22, t('briefing.cancel'), () => {
        close();
        options.onClose();
      }, { width: buttonWidth, height: 44, fontSize: 12 }),
      createButton(scene, panelWidth - 14 - buttonWidth / 2, y + 22, t(disabled ? 'briefing.depart.blocked' : abandoning === null ? 'briefing.depart' : 'briefing.redirect'), () => {
        if (disabled) return;
        close();
        options.onDepart({ incident: options.incident, vesselId: vessel.id, speed });
      }, { width: buttonWidth, height: 44, fontSize: 12 }).setAlpha(disabled ? 0.35 : 1),
    );

    const panelHeight = y + 58;
    frame.height = panelHeight;

    /*
     * Die Tafel gehoert an den Rand, nicht auf die Karte.
     *
     * Mittig gesetzt deckte sie die Karte praktisch vollstaendig zu — und damit
     * genau das, wofuer sie da ist: Man soll den gerechneten Kurs *sehen*, den
     * Umweg um die Baenke, statt ihn als Prozentzahl zu lesen. Die Karte ist
     * quadratisch und nach der kuerzeren Bildschirmkante eingepasst; auf einem
     * breiten Fenster liegen links und rechts davon mehrere hundert Pixel
     * ungenutzt. Dorthin.
     *
     * Rechts und nicht links, weil die Station am Westrand der Karte liegt und
     * die Kurse von dort nach Osten laufen — der Anfang des Kurses bleibt so
     * frei. Ist das Fenster zu schmal fuer eine eigene Spalte, bleibt es beim
     * alten Verhalten: mittig unten, denn dann gibt es ohnehin nichts zu
     * schonen.
     */
    const mapExtent = Math.min(width, height);
    const sideRoom = (width - mapExtent) / 2;
    const docked = sideRoom >= panelWidth * 0.55;
    const x = docked ? width - panelWidth - 16 : (width - panelWidth) / 2;
    const yTop = docked
      ? Math.max(16, (height - panelHeight) / 2)
      : height - panelHeight - 16;

    container = scene.add.container(x, yTop, parts);
    container.setDepth(1501);

    /*
     * Passt die Tafel nicht in die Hoehe — ein Telefon im Querformat hat keine
     * 400 Pixel —, wird sie als Ganzes verkleinert statt abgeschnitten. Die
     * Trefferflaechen wandern mit, und lesbar bleibt sie bis etwa 75 %.
     */
    const fit = Math.min(1, (height - 16) / panelHeight);
    if (fit < 1) {
      container.setScale(fit);
      container.setPosition((width - panelWidth * fit) / 2, 8);
    }

    maybeShowTutorial(scene, options.campaign, 'briefing');
  };

  function departureLine(): string {
    if (options.campaign.isAtBase(vessel.id)) return t('briefing.from.base');
    return t('briefing.from.sea', {
      miles: Math.round(options.campaign.distanceHome(vessel.id)),
    });
  }

  function buildVoyage(): Voyage {
    const condition = options.campaign.conditionOf(vessel.id);
    const start = options.campaign.positionOf(vessel.id);

    return new Voyage({
      route: options.campaign.routeTo(options.incident, vessel.draughtMetres, start),
      profile: vessel,
      from: start,
      to: options.incident.position,
      weather: options.campaign.weather,
      startHours: options.campaign.clockHours,
      fuel: condition.fuel,
      hull: condition.hull,
      speed,
      hullStressFactor: options.campaign.hullStressFactor,
      fuelFactor: options.campaign.fuelFactor,
    });
  }

  audio.resume();
  build();
}
